import WebComponent from '../../../common/WebComponent';
import {colors} from '../../../common/colors';
import {createShopDiscountIcon, ShopDiscountIcon} from '../../../common/svg';
import {Variant as DiscountIconVariant} from '../../../common/svg/shop-discount-icon';

export const ELEMENT_CLASS_NAME = 'shop-discount-code';
export const VARIANT_CODE_VISIBLE = 'code-visible';
export const VARIANT_SUBDUED = 'subdued';

const ATTRIBUTE_CODE = 'code';
const ATTRIBUTE_SAVED = 'saved';

export class ShopDiscountCode extends WebComponent {
  #rootElement: ShadowRoot;
  #wrapperElement!: HTMLDivElement;
  #codeElement!: HTMLSpanElement;
  #iconElement!: ShopDiscountIcon;
  #code: string | null = null;
  #saved = false;

  static get observedAttributes(): string[] {
    return [ATTRIBUTE_CODE, ATTRIBUTE_SAVED];
  }

  constructor() {
    super();

    const template = document.createElement('template');
    template.innerHTML = getStyle();

    this.#rootElement = this.attachShadow({mode: 'open'});
    this.#rootElement.appendChild(template.content.cloneNode(true));

    this.#wrapperElement = document.createElement('div');

    this.#iconElement = createShopDiscountIcon();
    this.#wrapperElement.appendChild(this.#iconElement);

    this.#codeElement = document.createElement('span');
    this.#wrapperElement.appendChild(this.#codeElement);

    this.#rootElement.appendChild(this.#wrapperElement);
  }

  connectedCallback(): void {
    const code = this.getAttribute(ATTRIBUTE_CODE);
    this._initCode(code);
    const saved = Boolean(this.getAttribute(ATTRIBUTE_SAVED) !== null);
    this._initSaved(saved);
  }

  attributeChangedCallback(
    name: string,
    _oldValue: string,
    newValue: string | null,
  ): void {
    const attributePassed = Boolean(newValue !== null);
    switch (name) {
      case ATTRIBUTE_CODE:
        this._initCode(newValue);
        break;
      case ATTRIBUTE_SAVED:
        this._initSaved(attributePassed);
        break;
    }
  }

  disconnectedCallback(): void {}

  private _updateStyles(): void {
    const classNames = [ELEMENT_CLASS_NAME];
    if (this.#code) {
      classNames.push(`${ELEMENT_CLASS_NAME}--${VARIANT_CODE_VISIBLE}`);
    }

    if (this.#saved) {
      this.#iconElement.setAttribute('variant', DiscountIconVariant.Branded);
    } else if (this.#code) {
      classNames.push(`${ELEMENT_CLASS_NAME}--${VARIANT_SUBDUED}`);
      this.#iconElement.setAttribute('variant', DiscountIconVariant.Subdued);
    } else {
      this.#iconElement.setAttribute('variant', DiscountIconVariant.Regular);
    }

    const className = classNames.join(' ');
    this.#wrapperElement.setAttribute('class', className);
  }

  private _initCode(code: string | null) {
    this.#codeElement.textContent = code;
    this.#code = code;
    this._updateStyles();
  }

  private _initSaved(saved: boolean) {
    this.#saved = saved;
    this._updateStyles();
  }
}

/**
 * function which returns the component's style
 * @returns {string} the style
 */
function getStyle(): string {
  return `
    <style>
      :host {
        display: flex;
        justify-content: center;
      }

      .${ELEMENT_CLASS_NAME} {
        background: ${colors.white};
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid ${colors.backgroundSubdued};
        border-radius: 100px;
        padding: 16px;
        transition: background-color 200ms ease-in-out;
      }

      .${ELEMENT_CLASS_NAME}--${VARIANT_CODE_VISIBLE} {
        padding: 16px 24px;
      }

      .${ELEMENT_CLASS_NAME}--${VARIANT_SUBDUED} {
        background: ${colors.backgroundSubdued};
      }

      span {
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0.06em;
        color: ${colors.foregroundSecondary};
        padding-left: 13px;
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      span:empty {
        padding-left: 0;
      }
    </style>
  `;
}

if (!customElements.get('shop-discount-code')) {
  customElements.define('shop-discount-code', ShopDiscountCode);
}

/**
 * helper function for building a DiscountCode component
 * @returns {ShopDiscountCode} a new DiscountCode instance
 */
export function createShopDiscountCode(): ShopDiscountCode {
  const element = document.createElement('shop-discount-code');
  return element as ShopDiscountCode;
}
